// frontend/src/components/WhatsApp/WhatsAppConversationPanel.jsx
// Hilo de conversación WhatsApp del engine (read-only). Slice 3 del spec master.
import React from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';
import { ChatBubbleLeftRightIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline';

const STATUS_LABELS = {
  queued: 'na fila',
  sent: 'enviado',
  delivered: 'entregue',
  read: 'lido',
  failed: 'falhou',
};

function formatDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function timeAgo(value) {
  if (!value) return null;
  const hours = Math.floor((Date.now() - new Date(value).getTime()) / 3600000);
  if (hours < 1) return 'há menos de 1h';
  if (hours < 24) return `há ${hours}h`;
  return `há ${Math.floor(hours / 24)} dia(s)`;
}

const WhatsAppConversationPanel = ({ phone }) => {
  const digits = String(phone || '').replace(/\D/g, '');

  const { data, isLoading, error } = useQuery(
    ['whatsapp-conversation', digits],
    async () => {
      const response = await api.get(`/whatsapp/conversation?phone=${digits}`);
      return response.data.data;
    },
    { enabled: digits.length >= 10, staleTime: 30000, retry: 1 }
  );

  if (!digits || digits.length < 10) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-green-600" /> Conversa WhatsApp
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Sem telefone cadastrado.</p>
      </div>
    );
  }

  const messages = data?.messages || [];
  const hsmSent = messages.some((m) => m.mensagem_hsm_id);
  const answered = !!data?.last_inbound_at;

  let badge = null;
  if (!isLoading && !error) {
    if (messages.length === 0) {
      badge = <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Nenhuma conversa</span>;
    } else if (answered) {
      badge = <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Respondeu {timeAgo(data.last_inbound_at)}</span>;
    } else if (hsmSent) {
      badge = <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">HSM enviado — sem resposta</span>;
    } else {
      badge = <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Sem resposta</span>;
    }
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-green-600" /> Conversa WhatsApp
        </h3>
        {badge}
      </div>

      {isLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Carregando conversa…</p>}

      {error && (
        <p className="text-sm text-red-600">
          Não foi possível carregar a conversa (engine indisponível). Tente novamente mais tarde.
        </p>
      )}

      {!isLoading && !error && messages.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma conversa no WhatsApp com este número.</p>
      )}

      {!isLoading && !error && messages.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.from_me ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                m.from_me
                  ? 'bg-green-100 dark:bg-green-900 text-gray-900 dark:text-gray-100'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
              }`}>
                {m.mensagem_hsm_id && (
                  <div className="text-[10px] uppercase tracking-wide text-green-700 dark:text-green-300 mb-0.5">Template HSM</div>
                )}
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <div className="flex items-center justify-end space-x-1 mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                  <ClockIcon className="h-3 w-3" />
                  <span>{formatDateTime(m.created_at)}</span>
                  {m.from_me && m.status && (
                    <span className="flex items-center"><CheckIcon className="h-3 w-3 ml-1" />{STATUS_LABELS[m.status] || m.status}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhatsAppConversationPanel;
